import { DateTime } from "luxon";

// ============================================================
// Règles de sous-orientation (CE-1419, maquette 11) : fonctions pures de
// l'écran, testées dans reglesSousOrientation.test.js. Une règle arrive de
// l'API sous sa forme stockée (`criteres`, `destination`) avec ses communes
// nommées, ses opérateurs et leur disponibilité par territoire. La saisie de
// la fenêtre est { sexe, rsaMajore, ageMin, ageMax, situations, communes:
// { [codeInsee]: [codes postaux retenus] }, operateurs, rang } ; une commune
// est choisie quand elle a une clé, entière quand tous ses codes sont retenus.
// ============================================================

const FUSEAU = "Indian/Reunion";

/** Les territoires de la Caf, avec leurs articles et le nombre de leurs communes. */
export const TERRITOIRES = [
  { id: "NORD", libelle: "Nord", le: "le Nord", du: "du Nord", nbCommunes: 3 },
  { id: "SUD", libelle: "Sud", le: "le Sud", du: "du Sud", nbCommunes: 10 },
  { id: "EST", libelle: "Est", le: "l'Est", du: "de l'Est", nbCommunes: 6 },
  { id: "OUEST", libelle: "Ouest", le: "l'Ouest", du: "de l'Ouest", nbCommunes: 5 },
];
const NB_COMMUNES = 24;

export const AGE_MIN = 16;
export const AGE_MAX = 99;

/** Les 13 situations familiales, accordées pour une phrase, en deux groupes : personne seule, en couple. */
export const SITUATIONS = [
  { id: "CEL", nom: "Célibataire", f: "célibataires", m: "célibataires", x: "célibataires", seule: true },
  { id: "DIV", nom: "Divorce", f: "divorcées", m: "divorcés", x: "divorcé(e)s", seule: true },
  { id: "VEU", nom: "Veuvage", f: "veuves", m: "veufs", x: "veuf(ve)s", seule: true },
  { id: "SEF", nom: "Séparation de fait", f: "séparées de fait", m: "séparés de fait", x: "séparé(e)s de fait", seule: true },
  { id: "SEL", nom: "Séparation légale", f: "séparées légalement", m: "séparés légalement", x: "séparé(e)s légalement", seule: true },
  { id: "ISO", nom: "Isolement après vie maritale ou PACS", f: "isolées après vie maritale ou PACS", m: "isolés après vie maritale ou PACS", x: "isolé(e)s après vie maritale ou PACS", seule: true },
  { id: "ABA", nom: "Disparu (jugement d'absence)", f: "au conjoint disparu", m: "au conjoint disparu", x: "au conjoint disparu", seule: true },
  { id: "MAR", nom: "Mariage", f: "mariées", m: "mariés", x: "marié(e)s" },
  { id: "PAC", nom: "PACS", f: "pacsées", m: "pacsés", x: "pacsé(e)s" },
  { id: "VIM", nom: "Vie maritale", f: "en vie maritale", m: "en vie maritale", x: "en vie maritale" },
  { id: "RVM", nom: "Reprise mariage", f: "en reprise de mariage", m: "en reprise de mariage", x: "en reprise de mariage" },
  { id: "RVC", nom: "Reprise vie maritale", f: "en reprise de vie maritale", m: "en reprise de vie maritale", x: "en reprise de vie maritale" },
  { id: "RPA", nom: "Reprise vie commune sur PACS", f: "en reprise de vie commune sur PACS", m: "en reprise de vie commune sur PACS", x: "en reprise de vie commune sur PACS" },
];
const situation = (id) => SITUATIONS.find((s) => s.id === id);

export const majuscule = (texte) => texte.charAt(0).toUpperCase() + texte.slice(1);

/** « 1 règle », « 3 règles ». */
export const pluriel = (n, mot, motPluriel = `${mot}s`) => `${n} ${n > 1 ? motPluriel : mot}`;

/** « a », « a et b », « a, b et c ». */
export function liste(elements, conjonction) {
  if (elements.length <= 1) return elements.join("");
  return `${elements.slice(0, -1).join(", ")} ${conjonction} ${elements[elements.length - 1]}`;
}

const critere = (criteres, type) => (criteres || []).find((c) => c.type === type) || null;
const genre = (criteres) => {
  const sexe = critere(criteres, "SEXE");
  return sexe ? (sexe.valeur === "FEMME" ? "f" : "m") : "x";
};

/** « femmes célibataires au RSA majoré, de 26 ans ou plus dans six mois », « tous les allocataires ». */
export function phrasePublic(criteres) {
  const g = genre(criteres);
  const situations = (critere(criteres, "SITUATION_FAMILIALE") || {}).valeurs || [];
  const rsa = critere(criteres, "RSA_MAJORE");
  const age = critere(criteres, "AGE_DANS_SIX_MOIS");
  let morceaux = [{ f: "femmes", m: "hommes", x: "allocataires" }[g]];
  const avecSituation = situations.length > 0 && situations.length < SITUATIONS.length;
  if (avecSituation) {
    const seules = SITUATIONS.filter((s) => s.seule);
    const toutesSeules = situations.length === seules.length && seules.every((s) => situations.includes(s.id));
    if (!toutesSeules && g === "x" && situations.length === 1) morceaux = [];
    morceaux.push(toutesSeules ? { f: "seules", m: "seuls", x: "seul(e)s" }[g] : liste(situations.map((id) => situation(id)[g]), "ou"));
  }
  if (rsa) morceaux.push(rsa.valeur ? "au RSA majoré" : "au RSA non majoré");
  let phrase = morceaux.length === 1 && g === "x" && !avecSituation ? "tous les allocataires" : morceaux.join(" ");
  if (age && age.min && age.max) phrase += `, de ${age.min} à ${age.max} ans dans six mois`;
  else if (age && age.min) phrase += `, de ${age.min} ans ou plus dans six mois`;
  else if (age && age.max) phrase += `, de ${age.max} ans ou moins dans six mois`;
  return phrase;
}

const parNom = (a, b) => a.nom.localeCompare(b.nom, "fr");

/**
 * Les communes d'une règle (forme de l'API : nom, territoire, codes retenus ou null), territoire par
 * territoire : un territoire entier se dit d'un mot, une commune réduite à certains codes le dit.
 */
function lieux(communes, rendu) {
  const restreinte = (c) => Array.isArray(c.codesPostaux);
  if (communes.length === NB_COMMUNES && !communes.some(restreinte)) return [[rendu.tout]];
  return TERRITOIRES.map((t) => {
    const dans = communes.filter((c) => c.territoire === t.id).sort(parNom);
    if (!dans.length) return null;
    const entier = dans.length === t.nbCommunes && !dans.some(restreinte);
    return entier ? [rendu.territoire(t)] : dans.map((c) => rendu.commune(c, restreinte(c)));
  }).filter(Boolean);
}
const parTerritoire = (groupes) => groupes.map((g) => g.join(", ")).join(" · ");

/** Dans la liste : chaque commune avec ses codes postaux, « seulement » quand elle est restreinte. */
export function communesTexte(communes) {
  return parTerritoire(lieux(communes, {
    tout: "Toutes les communes",
    territoire: (t) => `tout ${t.le}`,
    commune: (c, partiel) => `${c.nom} (${(partiel ? c.codesPostaux : c.tousCodesPostaux).join(", ")}${partiel ? " seulement" : ""})`,
  }));
}

/** Dans le schéma : les codes postaux d'une commune restreinte seulement. */
export function communesCourt(communes) {
  return parTerritoire(lieux(communes, {
    tout: "Toutes les communes",
    territoire: (t) => `tout ${t.le}`,
    commune: (c, partiel) => (partiel ? `${c.nom} (${c.codesPostaux.join(", ")})` : c.nom),
  }));
}

/** « habitant Saint-Denis ou Le Tampon (97430 seulement) », « habitant une commune du Nord ». */
export function communesPhrase(communes) {
  const parts = [].concat(...lieux(communes, {
    tout: "n'importe quelle commune",
    territoire: (t) => `une commune ${t.du}`,
    commune: (c, partiel) => (partiel ? `${c.nom} (${c.codesPostaux.join(", ")} seulement)` : c.nom),
  }));
  return `habitant ${liste(parts, "ou")}`;
}

const SUITE = "ces allocataires passent à la règle suivante, sinon restent à la Caf.";

/**
 * L'état d'une règle pour l'année des quotas : la consommation de ses opérateurs en service quand elle
 * s'applique, sinon pourquoi elle est passée (pas en service, sans quota, quota atteint), partout ou sur
 * une partie de ses territoires. `court` complète le schéma, `note` s'affiche sous la ligne.
 */
export function etatRegle(regle, annee) {
  const enService = new Set(regle.operateurs.filter((o) => o.enService).map((o) => o.id));
  if (!enService.size) {
    return {
      ton: "ko",
      pastille: { libelle: "Pas en service", icone: "fa-ban" },
      court: "pas en service",
      note: `Aucun opérateur de la règle n'est en service (en préparation ou désactivé) : ${SUITE}`,
    };
  }
  const dispos = regle.territoires.flatMap((t) => t.disponibilites).filter((d) => enService.has(d.operateurId));
  const avecQuota = dispos.filter((d) => d.quota !== null);
  if (!avecQuota.length) {
    return {
      ton: "warn",
      pastille: { libelle: "Sans quota", icone: "fa-exclamation-triangle" },
      court: "sans quota",
      note: `Aucun quota FSE+ ${annee} saisi : ${SUITE}`,
    };
  }
  if (regle.passee === "PARTOUT") {
    return {
      ton: "ko",
      pastille: { libelle: "Quota atteint", icone: "fa-times-circle" },
      court: "quota atteint",
      note: `Quota FSE+ ${annee} atteint ou non saisi : ${SUITE}`,
    };
  }
  const bloques = regle.territoires.filter((t) => t.passee).map((t) => t.libelle);
  return {
    ton: bloques.length ? "warn" : "ok",
    utilisees: avecQuota.reduce((somme, d) => somme + Math.min(d.utilisees, d.quota), 0),
    quota: avecQuota.reduce((somme, d) => somme + d.quota, 0),
    court: bloques.length ? `quota atteint (${liste(bloques, "et")})` : "",
    note: bloques.length
      ? `${majuscule(liste(bloques, "et"))} : quota atteint ou non saisi, ces allocataires-là passent à la règle suivante, sinon restent à la Caf.`
      : null,
  };
}

// ------------------------------------------------------------
// Saisie
// ------------------------------------------------------------

const communesDuReferentiel = (referentiel) => referentiel.territoires.flatMap((t) => t.communes.map((c) => ({ ...c, territoire: t.id })));
const codesDe = (commune) => commune.codesPostaux.map((cp) => cp.codePostal);

/** Une nouvelle règle : toutes les situations cochées, aucune commune, aucun opérateur, à la fin de la liste. */
export function saisieVide(referentiel) {
  return {
    sexe: "",
    rsaMajore: "",
    ageMin: "",
    ageMax: null,
    situations: referentiel.situationsFamiliales.map((s) => s.id),
    communes: {},
    operateurs: [],
    rang: null,
  };
}

/** La saisie d'une règle enregistrée ; un âge maximum (posé par l'API) est gardé tel quel. */
export function saisieDepuisRegle(regle, referentiel) {
  const sexe = critere(regle.criteres, "SEXE");
  const rsa = critere(regle.criteres, "RSA_MAJORE");
  const age = critere(regle.criteres, "AGE_DANS_SIX_MOIS");
  const lieu = critere(regle.criteres, "LIEU");
  const tous = new Map(communesDuReferentiel(referentiel).map((c) => [c.codeInsee, codesDe(c)]));
  return {
    sexe: sexe ? sexe.valeur : "",
    rsaMajore: rsa ? (rsa.valeur ? "oui" : "non") : "",
    ageMin: age && age.min ? String(age.min) : "",
    ageMax: age && age.max ? age.max : null,
    situations: [...((critere(regle.criteres, "SITUATION_FAMILIALE") || {}).valeurs || [])],
    communes: ((lieu && lieu.communes) || []).reduce(
      (communes, c) => ({ ...communes, [c.codeInsee]: c.codesPostaux ? [...c.codesPostaux] : tous.get(c.codeInsee) || [] }),
      {}
    ),
    operateurs: [...regle.destination.operateurIds],
    rang: regle.rang,
  };
}

const ageSaisi = (texte) => (/^\d+$/.test(String(texte).trim()) ? Number(String(texte).trim()) : NaN);

/** Les erreurs de la saisie, sous les mêmes clés que celles de l'API. */
export function validerSaisie(saisie) {
  const erreurs = {};
  if (String(saisie.ageMin).trim() !== "") {
    const age = ageSaisi(saisie.ageMin);
    if (!(age >= AGE_MIN && age <= AGE_MAX)) erreurs.age = `Indiquez un âge entre ${AGE_MIN} et ${AGE_MAX} ans, ou laissez vide.`;
  }
  if (!saisie.situations.length) erreurs.situationsFamiliales = "Cochez au moins une situation familiale.";
  if (!Object.keys(saisie.communes).length) erreurs.communes = "Choisissez au moins une commune.";
  if (!saisie.operateurs.length) erreurs.operateurs = "Choisissez au moins un opérateur.";
  return erreurs;
}

/**
 * Les erreurs à afficher : celles de l'écran tant que leur champ n'est pas corrigé, celles de l'API
 * (qui refuse ce que l'écran ne pouvait pas voir : un opérateur désactivé depuis) jusqu'au prochain envoi.
 */
export function erreursAffichees(erreursEcran, erreursApi, saisie) {
  const restantes = validerSaisie(saisie);
  const ecran = Object.fromEntries(Object.entries(erreursEcran).filter(([champ]) => champ in restantes));
  return { ...erreursApi, ...ecran };
}

/** Les critères de la saisie, sous leur forme stockée : seuls ceux qui restreignent, plus les situations et le lieu (requis). */
function criteresDeSaisie(saisie, referentiel) {
  const tous = new Map(communesDuReferentiel(referentiel).map((c) => [c.codeInsee, codesDe(c)]));
  const ageMin = String(saisie.ageMin).trim() === "" ? null : ageSaisi(saisie.ageMin);
  return [
    ...(saisie.sexe ? [{ type: "SEXE", valeur: saisie.sexe }] : []),
    { type: "SITUATION_FAMILIALE", valeurs: referentiel.situationsFamiliales.map((s) => s.id).filter((id) => saisie.situations.includes(id)) },
    ...(saisie.rsaMajore ? [{ type: "RSA_MAJORE", valeur: saisie.rsaMajore === "oui" }] : []),
    ...(ageMin !== null || saisie.ageMax ? [{ type: "AGE_DANS_SIX_MOIS", min: ageMin, max: saisie.ageMax || null }] : []),
    {
      type: "LIEU",
      communes: Object.keys(saisie.communes).sort().map((codeInsee) => {
        const retenus = saisie.communes[codeInsee];
        const tousCodes = tous.get(codeInsee) || [];
        return { codeInsee, codesPostaux: retenus.length >= tousCodes.length ? null : [...retenus].sort() };
      }),
    },
  ];
}

/** Le corps de la requête d'enregistrement d'une règle. */
export function corpsRequete(saisie, referentiel) {
  return {
    criteres: criteresDeSaisie(saisie, referentiel),
    destination: { type: "OPERATEURS", operateurIds: [...saisie.operateurs] },
    rang: saisie.rang,
  };
}

/** Les communes de la saisie, sous la forme de l'API, pour les phrases. */
function communesDeSaisie(saisie, referentiel) {
  return communesDuReferentiel(referentiel)
    .filter((c) => saisie.communes[c.codeInsee])
    .map((c) => {
      const tousCodes = codesDe(c);
      const retenus = saisie.communes[c.codeInsee];
      return { codeInsee: c.codeInsee, nom: c.nom, territoire: c.territoire, codesPostaux: retenus.length >= tousCodes.length ? null : [...retenus].sort(), tousCodesPostaux: tousCodes };
    });
}

const nomOperateur = (referentiel, id) => (referentiel.operateurs.find((o) => o.id === id) || { nom: "Opérateur inconnu" }).nom;

/** « En clair », en tête de la saisie : la règle telle qu'Espoar va l'appliquer. */
export function enClair(saisie, referentiel) {
  const criteres = criteresDeSaisie(saisie, referentiel);
  const accord = genre(criteres) === "f" ? "es" : "s";
  const publicDeLaRegle = phrasePublic(criteres);
  const sujet = /^tous les/.test(publicDeLaRegle) ? majuscule(publicDeLaRegle) : `Les ${publicDeLaRegle}`;
  const communes = communesDeSaisie(saisie, referentiel);
  const ou = communes.length ? `, ${communesPhrase(communes)}` : " (choisissez au moins une commune)";
  const noms = saisie.operateurs.map((id) => nomOperateur(referentiel, id));
  const vers = !noms.length
    ? `sont sous-orienté${accord} vers l'opérateur choisi`
    : noms.length === 1
      ? `sont sous-orienté${accord} vers ${noms[0]}`
      : `sont réparti${accord} à parts égales entre ${liste(noms, "et")}`;
  return `${sujet}${ou}, ${vers}, en accompagnement total, sur le quota FSE+ de leur territoire.`;
}

// ------------------------------------------------------------
// Cases des communes et des codes postaux
// ------------------------------------------------------------

/** « coche » (tous ses codes), « partiel » (certains), « vide ». */
export function etatCommune(saisie, commune) {
  const retenus = saisie.communes[commune.codeInsee];
  if (!retenus) return "vide";
  return retenus.length >= codesDe(commune).length ? "coche" : "partiel";
}

const avecCommunes = (saisie, communes) => ({ ...saisie, communes });

export function basculerCommune(saisie, commune, coche) {
  const { [commune.codeInsee]: _retiree, ...autres } = saisie.communes;
  return avecCommunes(saisie, coche ? { ...autres, [commune.codeInsee]: codesDe(commune) } : autres);
}

/** Retirer le dernier code postal d'une commune la retire. */
export function basculerCodePostal(saisie, commune, codePostal, coche) {
  const actuels = saisie.communes[commune.codeInsee] || [];
  const retenus = codesDe(commune).filter((cp) => (cp === codePostal ? coche : actuels.includes(cp)));
  const { [commune.codeInsee]: _retiree, ...autres } = saisie.communes;
  return avecCommunes(saisie, retenus.length ? { ...autres, [commune.codeInsee]: retenus } : autres);
}

export function basculerTerritoire(saisie, territoire, coche) {
  return territoire.communes.reduce((s, c) => basculerCommune(s, c, coche), saisie);
}

/** Les communes cochées d'un territoire (en tout ou en partie), et s'il l'est en entier. */
export function compteTerritoire(saisie, territoire) {
  const etats = territoire.communes.map((c) => etatCommune(saisie, c));
  return {
    cochees: etats.filter((e) => e !== "vide").length,
    total: territoire.communes.length,
    entier: etats.length > 0 && etats.every((e) => e === "coche"),
  };
}

// ------------------------------------------------------------
// Versions et historique
// ------------------------------------------------------------

/** « 2027-01-01 » → « 01/01/2027 ». */
export const jourFr = (jour) => DateTime.fromISO(jour).toFormat("dd/MM/yyyy");

/** La date du jour à La Réunion, « AAAA-MM-JJ ». */
export const aujourdhui = () => DateTime.now().setZone(FUSEAU).toISODate();

/** La version ouverte par défaut : celle en vigueur, sinon la plus récente. */
export function versionParDefaut(versions) {
  const enVigueur = versions.find((v) => v.statut.id === "EN_VIGUEUR");
  return (enVigueur || versions[0] || { id: null }).id;
}

export function libelleVersion(version) {
  const jour = jourFr(version.enVigueurLe);
  if (version.statut.id === "EN_VIGUEUR") return `En vigueur depuis le ${jour}`;
  if (version.statut.id === "A_VENIR") return `À venir au ${jour}`;
  return `Remplacée (${jour})`;
}

/** Une règle telle que le journal l'a recopiée, en une phrase. */
function phraseInstantane(regle) {
  const vers = regle.operateurs.length > 1 ? "répartis entre" : "vers";
  return `${phrasePublic(regle.criteres)}, habitant ${liste(regle.communes, "ou")}, ${vers} ${liste(regle.operateurs, "et")}`;
}

/** Une entrée du journal d'une version : quand (heure de La Réunion), qui, et ce qui a changé, en phrases. */
export function texteEntree(entree) {
  const p = entree.proprietes || {};
  const lignes = (() => {
    switch (entree.evenement.id) {
      case "VERSION_PREPAREE":
        if (!p.repriseDe) return [`Version au ${jourFr(p.enVigueurLe)} créée, sans règle.`];
        return [`Version au ${jourFr(p.enVigueurLe)} préparée, ${p.nombreRegles ? `avec ${p.nombreRegles > 1 ? `les ${p.nombreRegles} règles` : "la règle"} de la version en vigueur` : "sans règle"}.`];
      case "VERSION_SUPPRIMEE":
        return [`Version au ${jourFr(p.enVigueurLe)} supprimée.`];
      case "REGLE_AJOUTEE":
        return [`Règle ${p.apres.rang} ajoutée : ${phraseInstantane(p.apres)}.`];
      case "REGLE_DEPLACEE":
        return [`Règle déplacée de la position ${p.avant.rang} à la position ${p.apres.rang} : ${phraseInstantane(p.apres)}.`];
      case "REGLE_MODIFIEE":
        return [
          `Règle ${p.apres.rang} modifiée${p.avant.rang !== p.apres.rang ? ` (auparavant en position ${p.avant.rang})` : ""} : ${phraseInstantane(p.apres)}.`,
          `Avant : ${phraseInstantane(p.avant)}.`,
        ];
      case "REGLE_SUPPRIMEE":
        return [`Règle ${p.avant.rang} supprimée : ${phraseInstantane(p.avant)}.`];
      default:
        return [entree.evenement.libelle];
    }
  })();
  const le = DateTime.fromISO(entree.le).setZone(FUSEAU);
  return {
    quand: `${le.toFormat("dd/MM/yyyy")} à ${le.toFormat("HH:mm")}`,
    qui: entree.auteur ? entree.auteur.nom || "Utilisateur sans nom" : "Espoar",
    lignes,
  };
}
